import React, { useState, useEffect, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../styles/ag-grid-custom.css";
import { ColDef } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import {
  Search,
  Plus,
  Trash2,
  Eye,
  Edit,
  ArrowLeft,
  Calendar,
} from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermission";
import { PREVENTIVE_PREDICTIVE_MAINTENANCE } from "../../../constants/modules";
import { MaintenanceLog } from "../../../types/api";
import MaintenanceLogForm from "./MaintenanceLogForm";
import { useOrgUsersStore } from "../../../store/store";
import { getAssetInventory } from "../../../service/api";
import { usePreventiveMaintenanceStore } from "../../../store/utilitySystemsStore";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const MaintenanceLogList: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canRead, canUpdate, canDelete } = usePermissions();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<MaintenanceLog | null>(null);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAsset, setSelectedAsset] = useState<string>("");
  const [selectedTask, setSelectedTask] = useState<string>("");
  const [assets, setAssets] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const { orgUsersData, fetchOrgUsers, orgUsersMap } = useOrgUsersStore();
  const {
    maintenanceLogs,
    logsLoading,
    maintenanceTasks,
    tasksLoading,
    fetchMaintenanceLogs,
    fetchLogsByAsset,
    fetchLogsByTask,
    deleteMaintenanceLog: deleteLog,
    fetchMaintenanceTasks,
  } = usePreventiveMaintenanceStore();

  const fetchAssets = useCallback(async () => {
    try {
      const response = await getAssetInventory();
      if (response.data?.data) {
        setAssets(Array.isArray(response.data.data) ? response.data.data : []);
      } else if (response.data?.message) {
        setAssets(
          Array.isArray(response.data.message) ? response.data.message : []
        );
      }
    } catch (error) {
      console.error("Error fetching asset inventory:", error);
    }
  }, []);

  useEffect(() => {
    fetchOrgUsers();
    fetchAssets();
    fetchMaintenanceTasks();
  }, []);

  // useEffect(() => {
  //   setTasks(maintenanceTasks);
  // }, [maintenanceTasks]);

  useEffect(() => {
    const loadLogs = async () => {
      try {
        if (selectedAsset) {
          await fetchLogsByAsset(selectedAsset);
        } else if (selectedTask) {
          await fetchLogsByTask(selectedTask);
        } else {
          await fetchMaintenanceLogs();
        }
      } catch (error: any) {
        toast.error("Failed to fetch maintenance logs");
        console.error("Error fetching logs:", error);
      }
    };

    loadLogs();
  }, [selectedAsset, selectedTask]);

  const handleCreate = () => {
    setSelectedLog(null);
    setMode("create");
    setIsFormOpen(true);
  };

  const handleView = (log: MaintenanceLog) => {
    setSelectedLog(log);
    setMode("view");
    setIsFormOpen(true);
  };

  const handleEdit = (log: MaintenanceLog) => {
    setSelectedLog(log);
    setMode("edit");
    setIsFormOpen(true);
  };

  const handleDelete = async (log: MaintenanceLog) => {
    if (
      !window.confirm("Are you sure you want to delete this maintenance log?")
    ) {
      return;
    }

    try {
      const id = log.id || log._id;
      if (!id) {
        toast.error("Log ID not found");
        return;
      }

      await deleteLog(id);
      toast.success("Maintenance log deleted successfully");
      if (selectedAsset) {
        await fetchLogsByAsset(selectedAsset);
      } else if (selectedTask) {
        await fetchLogsByTask(selectedTask);
      } else {
        await fetchMaintenanceLogs();
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.resMessage || "Failed to delete maintenance log"
      );
    }
  };

  const handleFormSuccess = async () => {
    setIsFormOpen(false);
    try {
      if (selectedAsset) {
        await fetchLogsByAsset(selectedAsset);
      } else if (selectedTask) {
        await fetchLogsByTask(selectedTask);
      } else {
        await fetchMaintenanceLogs();
      }
    } catch (error: any) {
      console.error("Error refreshing logs:", error);
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "Asset",
      field: "assetName",
      flex: 1,
      cellRenderer: (params: any) => {
        const asset = assets.find(
          (a) => (a._id || a.id) === params.data.assetId
        );
        return (
          asset?.assetName ||
          asset?.name ||
          asset?.assetCode ||
          asset?.code ||
          params.data.assetName ||
          params.data.assetId ||
          "N/A"
        );
      },
    },
    {
      headerName: "Technician",
      field: "technicianName",
      flex: 1,
      cellRenderer: (params: any) => {
        return orgUsersMap.get(params.data.technicianId) || "N/A";
      },
    },
    {
      headerName: "Work Done",
      field: "workDone",
      flex: 2,
      cellRenderer: (params: any) => {
        const workDone = params.value || "";
        return workDone.length > 100
          ? `${workDone.substring(0, 100)}...`
          : workDone;
      },
    },
    {
      headerName: "Time Taken",
      field: "timeTaken",
      flex: 1,
      cellRenderer: (params: any) => {
        if (!params.data.timeTaken) return "N/A";
        const unit = params.data.timeTakenUnit || "hours";
        return `${params.data.timeTaken} ${unit}`;
      },
    },
    {
      headerName: "Date",
      field: "createdAt",
      flex: 1,
      cellRenderer: (params: any) => {
        if (!params.value) return "N/A";
        return new Date(params.value).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 150,
      pinned: "right",
      cellRenderer: (params: any) => {
        const log = params.data as MaintenanceLog;
        return (
          <div className="flex gap-2 items-center h-full">
            <button
              onClick={() => handleView(log)}
              className="text-blue-600 hover:text-blue-800 p-1"
              title="View"
            >
              <Eye size={18} />
            </button>
            {canUpdate(PREVENTIVE_PREDICTIVE_MAINTENANCE) && (
              <button
                onClick={() => handleEdit(log)}
                className="text-green-600 hover:text-green-800 p-1"
                title="Edit"
              >
                <Edit size={18} />
              </button>
            )}
            {canDelete(PREVENTIVE_PREDICTIVE_MAINTENANCE) && (
              <button
                onClick={() => handleDelete(log)}
                className="text-red-600 hover:text-red-800 p-1"
                title="Delete"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
  };

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/utility-systems/maintenance/schedules")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Schedules</span>
          </button>
          <h1 className="text-2xl font-semibold text-gray-800">
            Maintenance Logs
          </h1>
        </div>
        {canCreate(PREVENTIVE_PREDICTIVE_MAINTENANCE) && (
          <button
            onClick={handleCreate}
            className="bg-[#0fa9a6] text-white px-4 py-2 rounded-lg hover:bg-[#0d8f8c] flex items-center gap-2 transition-colors"
          >
            <Plus size={20} />
            Create Log
          </button>
        )}
      </div>

      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Asset
            </label>
            <select
              value={selectedAsset}
              onChange={(e) => {
                setSelectedAsset(e.target.value);
                setSelectedTask("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
            >
              <option value="">All Assets</option>
              {assets.map((asset) => (
                <option
                  key={asset._id || asset.id}
                  value={asset._id || asset.id}
                >
                  {asset.assetName ||
                    asset.name ||
                    asset.assetCode ||
                    asset.code ||
                    asset._id ||
                    asset.id}
                </option>
              ))}
            </select>
          </div>
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Task
            </label>
            <select
              value={selectedTask}
              onChange={(e) => {
                setSelectedTask(e.target.value);
                setSelectedAsset("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
            >
              <option value="">All Tasks</option>
              {maintenanceTasks.map((task: any) => (
                <option key={task._id || task.id} value={task._id || task.id}>
                  {`${task.assetName || task.assetId} - ${
                    task.status || "N/A"
                  }`}
                </option>
              ))}
            </select>
          </div> */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search logs..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div
          className="ag-theme-alpine"
          style={{ height: "600px", width: "100%" }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={maintenanceLogs}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={20}
            loading={logsLoading}
            animateRows={true}
            // rowSelection="single"
            rowHeight={50}
            headerHeight={65}
            getRowStyle={() => {
              return {
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                marginBottom: "8px",
                borderRadius: "4px",
                backgroundColor: "white",
              };
            }}
          />
        </div>
      </div>

      {isFormOpen && (
        <MaintenanceLogForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          log={selectedLog}
          mode={mode}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default MaintenanceLogList;
